import { CheckCircle2, Copy, IdCard, Link2, Send } from "lucide-react";
import type { CreateInviteeResponse } from "../types/invitee";
import { formatPhoneNumber } from "../utils/inviteeFormatters";
import { showSuccess } from "@/lib/utils/notifications";
import styles from "../styles/inviteeAdd.module.css";
import { PageHeader } from "./PageHeader";

interface InviteeSuccessViewProps {
  inviteCode: string;
  inviteDetails: CreateInviteeResponse;
  firstName: string;
  lastName: string;
  email?: string;
  mobileNo?: string;
  onStartNew: () => void;
  autoResubmit?: boolean;
}

/**
 * Angular parity (invite-add.component.html #sendView, lines 742-758):
 * Success view shows only "Start a new Invite" button.
 * There is NO "Apply Now" button in the success view.
 * "Apply Now" only exists on the invite form (pre-submit).
 */
export function InviteeSuccessView({
  inviteCode,
  inviteDetails,
  firstName,
  lastName,
  email,
  mobileNo,
  onStartNew,
  autoResubmit = false,
}: InviteeSuccessViewProps) {
  const handleCopyUrl = async () => {
    // Angular invite-add `copyUrl()`: full absolute URL so paste into a new tab works.
    // Relative `/apply/...` alone is not valid in the browser address bar.
    const url = `${window.location.origin}/apply/${inviteCode}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      showSuccess("Invitation Url Copy Successfully!");
    } catch {
      /* clipboard may be denied — still best-effort for older browsers */
    }
  };

  // Format phone number with + prefix like Angular
  const formattedPhone = mobileNo ? formatPhoneNumber(mobileNo) : "";

  return (
    <div
      data-testid="invitee-add-success-page"
      className={styles.successPageContainer}
    >
      <PageHeader title="INVITE" testIdRoot="invitee-add-success-header" />

      <div className={styles.contentWrap}>
        <div
          data-testid="invitee-add-success-content"
          className={styles.successContent}
        >
          <div className={styles.successMessageRow}>
            <div className={styles.successStatusIcon}>
              <CheckCircle2 className="h-7 w-7" aria-hidden="true" />
            </div>
            <div
              data-testid="invitee-add-success-message"
              className={styles.successMessage}
            >
              <h2>
                {autoResubmit
                  ? "Invitation will automatically resubmit after 30 days"
                  : "Invitation Sent Successfully!"}
              </h2>
              <p>
                Invitation has been sent to{" "}
                <b>
                  {firstName} {lastName}
                </b>
                {email && (
                  <>
                    {" "}on Email <b>{email}</b>
                  </>
                )}
                {mobileNo && (
                  <>
                    <br />
                    and on Mobile Phone <b>{formattedPhone}</b>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className={styles.successDivider} />

          <div className={styles.successDetailsRow}>
            <div
              data-testid="invitee-add-success-details"
              className={styles.successDetails}
            >
              {inviteDetails.lead_id && (
                <div className={styles.successDetailItem}>
                  <IdCard className={styles.successDetailIcon} aria-hidden="true" />
                  <div>
                    <span className={styles.successDetailLabel}>Lead ID</span>
                    <span
                      data-testid="invitee-add-success-lead-id"
                      className={styles.successDetailValue}
                    >
                      {inviteDetails.lead_id}
                    </span>
                  </div>
                </div>
              )}
              <div className={styles.successDetailItem}>
                <Link2 className={styles.successDetailIcon} aria-hidden="true" />
                <div>
                  <span className={styles.successDetailLabel}>Invite Code</span>
                  <span
                    data-testid={
                      inviteDetails.lead_id
                        ? "invitee-add-success-invite-code"
                        : "invitee-add-success-invite-code-only"
                    }
                    className={styles.successDetailValue}
                  >
                    {inviteCode}
                  </span>
                </div>
                <button
                  type="button"
                  data-testid="invitee-add-success-copy-url-button"
                  onClick={handleCopyUrl}
                  className={styles.copyUrlButton}
                >
                  <Copy className="h-4 w-4" aria-hidden="true" />
                  Copy
                </button>
              </div>
            </div>

            <div
              data-testid="invitee-add-success-actions"
              className={styles.successActions}
            >
              <button
                type="button"
                data-testid="invitee-add-success-start-new-button"
                onClick={onStartNew}
                className={styles.startNewButton}
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                Start a new Invite
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
